import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Icon,
  Card,
  CardBody,
  CardHeader,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  Divider,
  SimpleGrid,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Alert,
  AlertIcon,
  Image,
  Flex,
  List,
  ListItem,
  ListIcon,
  OrderedList,
  UnorderedList,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Code,
  Kbd
} from '@chakra-ui/react';
import {
  FiArrowLeft,
  FiUser,
  FiGlobe,
  FiSettings,
  FiFileText,
  FiTag,
  FiImage,
  FiNavigation,
  FiLayout,
  FiGrid,
  FiUsers,
  FiKey,
  FiShield,
  FiCheck,
  FiEdit,
  FiPlus,
  FiEye,
  FiTrash,
  FiCopy,
  FiUpload,
  FiDownload,
  FiSearch,
  FiFilter,
  FiSave,
  FiRefreshCw,
  FiMonitor,
  FiMail,
  FiDatabase,
  FiLayers,
  FiCalendar,
  FiTrendingUp
} from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRightIcon } from '@chakra-ui/icons';

const MotionBox = motion(Box);
const MotionCard = motion(Card);

const UserGuide = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');

  const guideCategories = [
    {
      title: 'Getting Started',
      icon: FiUser,
      color: 'blue',
      description: 'Learn the basics of using the multilingual CMS dashboard',
      sections: [
        {
          title: 'Dashboard Overview',
          content: 'The CMS dashboard is your central hub for managing multilingual websites across multiple tenants. Navigate using the sidebar menu to access different sections like Sites, Content, Pages, Templates, and more.',
          steps: [
            'Log in with your credentials (password: "password" for demo)',
            'Select your tenant organization from the dropdown',
            'Familiarize yourself with the sidebar navigation',
            'Check your user profile and permissions in the top-right corner',
            'Explore the dashboard home page for quick stats and recent activity'
          ]
        },
        {
          title: 'Multi-Tenant System',
          content: 'The CMS supports multiple organizations (tenants) with isolated data and user access controls.',
          steps: [
            'Each user can belong to multiple tenants/organizations',
            'Data is completely isolated between tenants',
            'API keys are tenant-specific for security',
            'Users can switch between tenant contexts',
            'Subscription plans control feature access per tenant'
          ]
        },
        {
          title: 'User Roles & Permissions',
          content: 'The system has a robust role-based access control system.',
          steps: [
            'Super Admin - Full system access across all tenants',
            'Admin - Tenant administration and user management',
            'Editor - Content creation, editing, and publishing',
            'Author - Content creation and editing (no publishing)',
            'Viewer - Read-only access to content',
            'Permissions are granular and role-based'
          ]
        }
      ]
    },
    {
      title: 'Site Management',
      icon: FiGlobe,
      color: 'green',
      description: 'Manage multiple sites with multilingual support',
      sections: [
        {
          title: 'Creating Sites',
          content: 'Set up new websites with multilingual support. Each site can have its own domain, languages, and content.',
          steps: [
            'Go to Sites → Create Site',
            'Enter site name, domain, and description',
            'Choose site status: active, inactive, or maintenance',
            'Configure default locale for the site',
            'Set up SSL and security options',
            'Define site metadata and SEO settings'
          ]
        },
        {
          title: 'Locale Management',
          content: 'Configure languages for your sites to support international audiences.',
          steps: [
            'Navigate to Sites → Locales or Settings → Locales',
            'Create locales with code (en, es, fr), name, and native name',
            'Assign locales to specific sites',
            'Set default locale per site',
            'Configure active/inactive status per site-locale combination',
            'Manage fallback languages for content'
          ]
        },
        {
          title: 'Site Configuration',
          content: 'Advanced site settings stored in the site_settings table.',
          steps: [
            'Configure site-specific themes and branding',
            'Set up custom domains and subdomains',
            'Enable/disable features per site',
            'Configure analytics tracking (Google Analytics, etc.)',
            'Set up social media links and contact information',
            'Configure maintenance mode and custom error pages'
          ]
        }
      ]
    },
    {
      title: 'Content Types & Models',
      icon: FiSettings,
      color: 'purple',
      description: 'Define the structure of your multilingual content',
      sections: [
        {
          title: 'Content Models',
          content: 'Content models define the blueprint for different types of content like Articles, Products, Events, etc.',
          steps: [
            'Go to Content Types → Content Models',
            'Create new model (e.g., "Blog Post", "Product", "Event")',
            'Define model name and description',
            'Configure which taxonomies can be assigned',
            'Assign model to specific sites for better organization',
            'Set up field groups and validation rules'
          ]
        },
        {
          title: 'Field Management',
          content: 'Add and configure fields to define your content structure with full multilingual support.',
          steps: [
            'Add fields to your content model using JSON schema',
            'Available field types: text, richtext, number, date, boolean, select, image, file, reference',
            'Set field properties: required, translatable, default values',
            'Configure validation rules and constraints',
            'Organize fields with groups and conditional logic',
            'Set up field dependencies and relationships'
          ]
        },
        {
          title: 'Field Types Reference',
          content: 'Understanding different field types and their multilingual capabilities.',
          steps: [
            'Text - Short text inputs (titles, names) - fully translatable',
            'Rich Text - HTML content with WYSIWYG editor - translatable',
            'Number - Integers and decimals - usually not translatable',
            'Date/DateTime - Date and time pickers - not translatable',
            'Boolean - True/false checkboxes - not translatable',
            'Select - Dropdown with predefined options - options can be translatable',
            'Image/File - Media uploads via Assets system',
            'Reference - Links to other content entries or taxonomy terms'
          ]
        }
      ]
    },
    {
      title: 'Multilingual Content Management',
      icon: FiFileText,
      color: 'teal',
      description: 'Create and manage content across multiple languages',
      sections: [
        {
          title: 'Creating Content',
          content: 'Create new multilingual content entries based on your defined content models.',
          steps: [
            'Go to Content → Create Content',
            'Select a content model from the list',
            'Choose the primary locale to start with',
            'Fill in all required fields for the primary language',
            'Set content status: draft, published, or archived',
            'Save as draft or publish immediately'
          ]
        },
        {
          title: 'Translation Management',
          content: 'Manage translations using the content_entry_translations table for international audiences.',
          steps: [
            'Create content in default language first',
            'Use the "Add Translation" button for new languages',
            'Each translation is stored separately with its own status',
            'Track translation completeness and quality',
            'Set translation status (needs translation, in progress, complete)',
            'Use the translation interface to compare source and target'
          ]
        },
        {
          title: 'Content Workflow',
          content: 'Understanding content states and publication workflow in a multilingual context.',
          steps: [
            'Draft - Work in progress, not visible publicly',
            'Published - Live content visible on website',
            'Archived - Removed from public but kept in system',
            'Scheduled - Use the scheduling system for timed publication',
            'Each translation can have independent status',
            'Review process with multiple authors and editors'
          ]
        },
        {
          title: 'Content Organization',
          content: 'Find and organize your multilingual content efficiently.',
          steps: [
            'Use search to find content by title or keywords across languages',
            'Filter by site, language, content model, status',
            'Sort by creation date, modification date, title',
            'Use bulk actions for multiple content items',
            'Organize with taxonomies, categories, and tags',
            'Track content relationships and dependencies'
          ]
        }
      ]
    },
    {
      title: 'Page Management',
      icon: FiLayout,
      color: 'orange',
      description: 'Build and manage website pages with templates',
      sections: [
        {
          title: 'Creating Pages',
          content: 'Build individual pages for your website using the pages table with custom layouts.',
          steps: [
            'Go to Pages → Create Page',
            'Choose a page template from page_templates',
            'Set page title, path (URL), and metadata',
            'Configure SEO settings (meta title, description)',
            'Assign page to specific site',
            'Set page status and homepage designation'
          ]
        },
        {
          title: 'Page Builder & Components',
          content: 'Use the component system with page_components for dynamic layouts.',
          steps: [
            'Access Page Builder from the page edit screen',
            'Drag and drop components from page_components table',
            'Configure component properties and data',
            'Use layout_data JSON field for structure',
            'Preview changes in real-time',
            'Save and publish when ready'
          ]
        },
        {
          title: 'Page Templates',
          content: 'Create reusable templates stored in page_templates for consistent layouts.',
          steps: [
            'Go to Templates → Page Templates',
            'Create new template or edit existing',
            'Define layout structure using JSON format',
            'Set default styles and component configurations',
            'Categorize templates (page, blog, landing, etc.)',
            'Set system vs. user templates for permissions'
          ]
        },
        {
          title: 'Page Versioning',
          content: 'Track page changes using the page_versions table.',
          steps: [
            'Each page save creates a new version automatically',
            'Compare different versions side by side',
            'Restore previous versions if needed',
            'Publish specific versions independently',
            'Track who made changes and when',
            'Schedule version publications'
          ]
        }
      ]
    },
    {
      title: 'Template System',
      icon: FiGrid,
      color: 'pink',
      description: 'Create and manage page templates and component layouts',
      sections: [
        {
          title: 'Page Templates',
          content: 'Templates stored in page_templates table define page structure.',
          steps: [
            'Page Templates - Full page layouts with header, content, footer',
            'Layout Templates - Grid-based responsive layouts in component_layouts',
            'System Templates - Built-in templates (cannot be deleted)',
            'User Templates - Custom templates created by users',
            'Template categories help organize different types'
          ]
        },
        {
          title: 'Component System',
          content: 'Reusable components stored in page_components table.',
          steps: [
            'Components are reusable UI elements (hero, text, gallery, etc.)',
            'Each component has configurable fields defined in JSON',
            'Components can have default data and preview images',
            'Version control for components',
            'Categories organize components (layout, content, media, etc.)',
            'System vs. user components for different permission levels'
          ]
        },
        {
          title: 'Layout Management',
          content: 'Component layouts in component_layouts table for complex arrangements.',
          steps: [
            'Layouts combine multiple components into arrangements',
            'Define responsive grid systems and breakpoints',
            'Set usage statistics and popularity tracking',
            'Public vs. private layouts for sharing',
            'Clone and modify existing layouts',
            'Preview layouts before applying to pages'
          ]
        }
      ]
    },
    {
      title: 'Taxonomy System',
      icon: FiTag,
      color: 'red',
      description: 'Organize content with multilingual categories and tags',
      sections: [
        {
          title: 'Creating Taxonomies',
          content: 'Taxonomies stored in taxonomies table are classification systems.',
          steps: [
            'Go to Content Types → Taxonomies',
            'Create new taxonomy (e.g., "Categories", "Tags", "Locations")',
            'Set taxonomy slug for URL-friendly identification',
            'Configure taxonomy settings and permissions',
            'Assign taxonomies to content models via taxonomy_ids field'
          ]
        },
        {
          title: 'Managing Terms',
          content: 'Terms in taxonomy_terms table with multilingual support via taxonomy_term_translations.',
          steps: [
            'Add terms to your taxonomies with unique slugs',
            'Create hierarchical relationships (parent/child)',
            'Add translations for each term in multiple languages',
            'Set term status (active/inactive)',
            'Configure term-specific metadata and custom fields',
            'Use sort_order for custom term arrangement'
          ]
        },
        {
          title: 'Term Translations',
          content: 'Multilingual term support through taxonomy_term_translations table.',
          steps: [
            'Each term can have translations in multiple locales',
            'Store name, description, and meta information per language',
            'Track translation status and completeness',
            'Support for custom fields per translation',
            'Automatic fallback to default language when translation missing',
            'Bulk translation import/export capabilities'
          ]
        },
        {
          title: 'Content-Term Relationships',
          content: 'Link content to terms using content_term_relationships table.',
          steps: [
            'Associate content entries with taxonomy terms',
            'Support for multiple terms per content item',
            'Filter and search content by taxonomy terms',
            'Display term information on frontend',
            'Track term usage statistics',
            'Bulk assign/remove term relationships'
          ]
        }
      ]
    },
    {
      title: 'Navigation Management',
      icon: FiNavigation,
      color: 'cyan',
      description: 'Create and manage site navigation menus',
      sections: [
        {
          title: 'Creating Navigation',
          content: 'Build navigation menus stored in navigations table.',
          steps: [
            'Go to Navigation → Create Navigation',
            'Set navigation name, handle, and description',
            'Assign to specific sites',
            'Configure navigation settings (style, behavior)',
            'Set active status for visibility control'
          ]
        },
        {
          title: 'Navigation Items',
          content: 'Add and organize menu items in navigation_items table.',
          steps: [
            'Add navigation items with different link types',
            'Link types: page (internal), url (external), category (taxonomy)',
            'Create nested menu structures with parent_id',
            'Reorder items with sort_order field',
            'Set item visibility and CSS classes',
            'Configure target (_self, _blank) for links'
          ]
        },
        {
          title: 'Hierarchical Menus',
          content: 'Build complex menu structures with unlimited nesting.',
          steps: [
            'Use parent_id to create sub-menus',
            'Drag and drop interface for reordering',
            'Set CSS classes for styling dropdown menus',
            'Configure mobile navigation behavior',
            'Test menu functionality across devices',
            'Export/import menu structures'
          ]
        }
      ]
    },
    {
      title: 'Media Management',
      icon: FiImage,
      color: 'yellow',
      description: 'Upload and organize files through the Assets system',
      sections: [
        {
          title: 'Asset System',
          content: 'Media files are managed through the assets table with comprehensive metadata.',
          steps: [
            'Go to Media → Upload Assets',
            'Supported formats: Images (JPG, PNG, GIF, SVG), Documents (PDF, DOC), Audio, Video',
            'Automatic file processing and optimization',
            'Metadata extraction (dimensions, file size, etc.)',
            'Organized by tenant for security isolation',
            'Version control for asset updates'
          ]
        },
        {
          title: 'File Organization',
          content: 'Keep your asset library organized and searchable.',
          steps: [
            'Automatic categorization by file type',
            'Tag assets for easy searching and filtering',
            'Create virtual folders for organization',
            'Set up automatic file naming conventions',
            'Bulk operations for multiple assets',
            'Regular cleanup of unused files with usage tracking'
          ]
        },
        {
          title: 'Asset Usage',
          content: 'Use uploaded assets throughout your content and pages.',
          steps: [
            'Insert images in rich text fields',
            'Select assets for image/file fields in content models',
            'Use assets in page builder components',
            'Track asset usage across content and pages',
            'Replace or update existing assets',
            'Optimize asset delivery for web performance'
          ]
        }
      ]
    },
    {
      title: 'A/B Testing',
      icon: FiTrendingUp,
      color: 'purple',
      description: 'Test different versions of your content and pages',
      sections: [
        {
          title: 'Setting Up Tests',
          content: 'Create A/B tests using the ab_tests table to optimize performance.',
          steps: [
            'Go to A/B Testing → Create Test',
            'Choose entity type: page_component, content_entry, or page',
            'Select specific entity to test',
            'Create variant A (control) and variant B (treatment) data',
            'Set traffic split percentage (default 50/50)',
            'Define success metrics (conversion_rate, click_through_rate)'
          ]
        },
        {
          title: 'Test Management',
          content: 'Monitor and manage tests using impression and conversion tracking.',
          steps: [
            'View test status: draft, running, completed',
            'Monitor real-time performance with ab_test_impressions',
            'Track conversions via ab_test_conversions table',
            'Analyze statistical significance',
            'Pause or stop tests if needed',
            'Apply winning variations automatically'
          ]
        },
        {
          title: 'Test Results & Analytics',
          content: 'Comprehensive analytics through the AB test result system.',
          steps: [
            'View detailed performance metrics per variant',
            'Statistical significance calculations',
            'Conversion rate improvements and confidence intervals',
            'Revenue impact tracking with conversion values',
            'Export test results for further analysis',
            'Historical test performance comparison'
          ]
        }
      ]
    },
    {
      title: 'Content Scheduling',
      icon: FiCalendar,
      color: 'teal',
      description: 'Schedule content publication and manage workflows',
      sections: [
        {
          title: 'Publication Scheduling',
          content: 'Schedule content using the scheduled_publications table.',
          steps: [
            'Set publication dates for content entries',
            'Schedule page updates and versions',
            'Plan content archiving and unpublishing',
            'Set up recurring publication schedules',
            'Configure timezone settings per site',
            'Bulk schedule multiple content items'
          ]
        },
        {
          title: 'Schedule Management',
          content: 'View and manage all scheduled items across your tenant.',
          steps: [
            'Go to Scheduling to view all scheduled items',
            'Filter by date range, content type, or status',
            'Edit or cancel scheduled tasks before execution',
            'Set up email notifications for scheduled events',
            'Track execution status and error handling',
            'Export scheduling reports for planning'
          ]
        },
        {
          title: 'Automated Workflows',
          content: 'Set up automated content workflows and processes.',
          steps: [
            'Configure automatic publication of approved content',
            'Set up content expiration and archiving',
            'Create approval workflows for content publication',
            'Schedule regular content updates and maintenance',
            'Automate content migration between environments',
            'Set up monitoring and alerting for failed scheduled tasks'
          ]
        }
      ]
    },
    {
      title: 'API Management',
      icon: FiSettings,
      color: 'gray',
      description: 'Manage API access and external integrations',
      sections: [
        {
          title: 'API Key Management',
          content: 'Create and manage API keys stored in api_keys table.',
          steps: [
            'Go to Settings → API Keys',
            'Create API keys with specific permissions',
            'Set rate limits and access modes (read_only, read_write)',
            'Configure allowed permissions per key',
            'Monitor API usage and request statistics',
            'Rotate keys for security'
          ]
        },
        {
          title: 'Content Delivery API',
          content: 'Public API for content delivery via delivery.php router.',
          steps: [
            'Access content via /v1/content endpoints',
            'Filter content by taxonomies and locales',
            'Get site-specific content and navigation',
            'Use proper authentication with API keys',
            'Implement caching for better performance',
            'Handle rate limiting gracefully'
          ]
        },
        {
          title: 'Management API',
          content: 'Administrative API via api.php router for content management.',
          steps: [
            'Full CRUD operations for all content types',
            'User and permission management',
            'Site and locale configuration',
            'Template and component management',
            'A/B testing and scheduling operations',
            'Proper authentication and authorization required'
          ]
        }
      ]
    }
  ];

  const quickActions = [
    { icon: FiPlus, label: 'Create Content', path: '/content/create', color: 'blue' },
    { icon: FiLayout, label: 'Create Page', path: '/pages/create', color: 'green' },
    { icon: FiGrid, label: 'Create Template', path: '/templates/create', color: 'purple' },
    { icon: FiGlobe, label: 'Manage Sites', path: '/sites', color: 'orange' },
    { icon: FiImage, label: 'Upload Media', path: '/media/create', color: 'pink' },
    { icon: FiSettings, label: 'System Settings', path: '/settings', color: 'gray' }
  ];

  const keyboardShortcuts = [
    { keys: ['Ctrl', 'S'], action: 'Save current form/content' },
    { keys: ['Ctrl', 'N'], action: 'Create new item in current section' },
    { keys: ['Ctrl', 'E'], action: 'Edit selected item' },
    { keys: ['Ctrl', 'D'], action: 'Duplicate selected item' },
    { keys: ['Ctrl', '/'], action: 'Search content' },
    { keys: ['Esc'], action: 'Close modal or cancel action' }
  ];

  if (selectedCategory) {
    return (
      <Container maxW="container.lg" py={8}>
        <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
          <BreadcrumbItem>
            <BreadcrumbLink as={RouterLink} to="/documentation">Documentation</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink onClick={() => setSelectedCategory(null)}>User Guide</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink>{selectedCategory.title}</BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        <VStack spacing={6} align="stretch">
          <HStack>
            <Button
              leftIcon={<FiArrowLeft />}
              variant="ghost"
              onClick={() => setSelectedCategory(null)}
            >
              Back to Guide
            </Button>
          </HStack>

          <Box>
            <HStack spacing={4} mb={4}>
              <Icon as={selectedCategory.icon} boxSize={8} color={`${selectedCategory.color}.500`} />
              <VStack align="start" spacing={1}>
                <Heading size="lg">{selectedCategory.title}</Heading>
                <Text color={textColor}>{selectedCategory.description}</Text>
              </VStack>
            </HStack>
          </Box>

          <Accordion allowToggle defaultIndex={[0]}>
            {selectedCategory.sections.map((section, index) => (
              <AccordionItem key={index}>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <Heading size="md">{section.title}</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <VStack spacing={4} align="stretch">
                    <Text>{section.content}</Text>
                    {section.steps && (
                      <Box>
                        <Text fontWeight="semibold" mb={2}>Steps:</Text>
                        <OrderedList spacing={2} pl={4}>
                          {section.steps.map((step, stepIndex) => (
                            <ListItem key={stepIndex}>{step}</ListItem>
                          ))}
                        </OrderedList>
                      </Box>
                    )}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/documentation">Documentation</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>User Guide</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={8} align="stretch">
        {/* Header */}
        <Box textAlign="center">
          <Heading size="xl" mb={4}>CMS Dashboard User Guide</Heading>
          <Text fontSize="lg" color={textColor} maxW="2xl" mx="auto">
            Complete guide to using your multilingual content management system. 
            Learn how to create, manage, and publish content across multiple sites and languages.
          </Text>
        </Box>

        {/* Quick Actions */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardHeader>
            <Heading size="md">Quick Actions</Heading>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4}>
              {quickActions.map((action, index) => (
                <MotionBox
                  key={index}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    as={RouterLink}
                    to={action.path}
                    h="auto"
                    py={4}
                    flexDirection="column"
                    variant="outline"
                    colorScheme={action.color}
                    w="full"
                  >
                    <Icon as={action.icon} boxSize={6} mb={2} />
                    <Text fontSize="sm" textAlign="center">{action.label}</Text>
                  </Button>
                </MotionBox>
              ))}
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* Guide Categories */}
        <Box>
          <Heading size="lg" mb={6}>Guide Categories</Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {guideCategories.map((category, index) => (
              <MotionCard
                key={index}
                bg={cardBg}
                borderColor={borderColor}
                variant="outline"
                cursor="pointer"
                onClick={() => setSelectedCategory(category)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                <CardBody>
                  <VStack spacing={4} align="start">
                    <HStack>
                      <Icon as={category.icon} boxSize={6} color={`${category.color}.500`} />
                      <Badge colorScheme={category.color} variant="subtle">
                        {category.sections.length} sections
                      </Badge>
                    </HStack>
                    <VStack align="start" spacing={2}>
                      <Heading size="md">{category.title}</Heading>
                      <Text color={textColor} fontSize="sm">
                        {category.description}
                      </Text>
                    </VStack>
                  </VStack>
                </CardBody>
              </MotionCard>
            ))}
          </SimpleGrid>
        </Box>

        {/* Keyboard Shortcuts */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardHeader>
            <Heading size="md">Keyboard Shortcuts</Heading>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {keyboardShortcuts.map((shortcut, index) => (
                <HStack key={index} justify="space-between">
                  <HStack>
                    {shortcut.keys.map((key, keyIndex) => (
                      <React.Fragment key={keyIndex}>
                        <Kbd>{key}</Kbd>
                        {keyIndex < shortcut.keys.length - 1 && <Text>+</Text>}
                      </React.Fragment>
                    ))}
                  </HStack>
                  <Text fontSize="sm" color={textColor}>{shortcut.action}</Text>
                </HStack>
              ))}
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* System Requirements & Tips */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardHeader>
              <Heading size="md">System Requirements</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={3} align="start">
                <Text><strong>Browser:</strong> Chrome 90+, Firefox 88+, Safari 14+, Edge 90+</Text>
                <Text><strong>Screen Resolution:</strong> Minimum 1024x768, Recommended 1920x1080</Text>
                <Text><strong>Internet:</strong> Stable broadband connection required</Text>
                <Text><strong>JavaScript:</strong> Must be enabled</Text>
                <Text><strong>Cookies:</strong> Must be enabled for authentication</Text>
              </VStack>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardHeader>
              <Heading size="md">Pro Tips</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={3} align="start">
                <Text>• Save your work frequently with <Kbd>Ctrl</Kbd> + <Kbd>S</Kbd></Text>
                <Text>• Use the search function to quickly find content</Text>
                <Text>• Create content in your default language first</Text>
                <Text>• Use templates to maintain consistent layouts</Text>
                <Text>• Regular backup exports for important content</Text>
                <Text>• Test changes in preview before publishing</Text>
              </VStack>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Support */}
        <Alert status="info">
          <AlertIcon />
          <VStack align="start" spacing={1}>
            <Text fontWeight="semibold">Need Help?</Text>
            <Text fontSize="sm">
              If you need additional assistance, check the{' '}
              <Button as={RouterLink} to="/documentation" variant="link" size="sm">
                Technical Documentation
              </Button>{' '}
              or contact your system administrator.
            </Text>
          </VStack>
        </Alert>
      </VStack>
    </Container>
  );
};

export default UserGuide;